import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Dropdown, Modal } from 'react-bootstrap'
import DatePicker from "react-datepicker"
import Select from 'react-select'
//import XLSX from 'xlsx'

import FilterSearch from 'pages/parts/FilterSearch'
import ProductCreateUpdate from 'pages/main/products/CreateUpdate'

import { httpOk, GET } from 'helpers/api'
import { formatMoney, returnSign, findKeyFromArrayByValue, formatDate } from 'helpers/helpers'
import { buildMenuProductCounts, buildMenuProductTreeRows } from 'helpers/productTreeView'

function Calculation() {
	const { t } = useTranslation()
	const reduxAccount = useSelector(state => state.account)

	const [poses, setPoses] = useState([])
	const [menu, setMenu] = useState([])
	const [categories, setCategories] = useState([])
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [expandedMenuIds, setExpandedMenuIds] = useState(() => new Set())
	const [selectedProduct, setSelectedProduct] = useState(null)
	const [filterData, setFilterData] = useState({
		pos_id: reduxAccount.pos_id,
		menu_ids: [],
		category_id: null,
		date: null,
		search: '',
	})
	const [data, setData] = useState([])

	async function searchWithFilter(nextFilterData = null) {
		var filterUrl = "/report-calculation"
		var urlParams = ""
		checkFilter()
		const currentFilterData = nextFilterData || filterData

		if (currentFilterData.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + currentFilterData.pos_id
		if (currentFilterData.menu_ids && currentFilterData.menu_ids.length > 0) {
			const ids = currentFilterData.menu_ids.map(item => item.id).join(',');
			urlParams += returnSign(urlParams) + 'menu_ids=' + ids;
		}
		if (currentFilterData.category_id)
			urlParams += returnSign(urlParams) + 'category_id=' + currentFilterData.category_id
		if (currentFilterData.date)
			urlParams += returnSign(urlParams) + 'date=' + currentFilterData.date
		if (currentFilterData.search)
			urlParams += returnSign(urlParams) + 'search=' + currentFilterData.search

		if (`/report-calculation${urlParams}` !== filterUrl)
			setFilterDataExist(true)

		filterUrl = filterUrl + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) setData(response.data)

		setDropdownIsOpen(false)
	}

	function handleSearchChange(search) {
		const nextFilterData = { ...filterData, search, page: null }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	function resetFilter() {
		setFilterData({
			pos_id: reduxAccount.pos_id,
			menu_ids: [],
			category_id: null,
			date: null,
			search: '',
		})
		setFilterDataExist(false)
		getData()
	}

	async function getData() {
		const response = await GET("/report-calculation", {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	function checkFilter() {
		if (
			filterData.pos_id === null &&
			(!filterData.menu_ids || filterData.menu_ids.length === 0) &&
			filterData.category_id === null &&
			filterData.date === null &&
			filterData.search === ''
		) {
			setFilterDataExist(false)
		}
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	async function getPoses() {
		const response = await GET('/helper/poses')
		if (httpOk(response)) setPoses(response.data)
	}

	async function getMenu() {
		var response = await GET('/helper/menu')
		if (httpOk(response)) setMenu(response.data)
	}

	async function getCategories() {
		const response = await GET('/helper/categories')
		if (httpOk(response)) setCategories(response.data)
	}

	function toggleMenu(menuId) {
		setExpandedMenuIds((previous) => {
			const next = new Set(previous)
			if (next.has(menuId)) next.delete(menuId)
			else next.add(menuId)
			return next
		})
	}

	const reportMenu = useMemo(() => {
		const menuById = new Map((menu || []).map((item) => [Number(item.id), item]))
		const visibleIds = new Set()

		for (const product of data || []) {
			let menuId = Number(product.menu_id)
			const seen = new Set()
			while (menuId && !visibleIds.has(menuId) && !seen.has(menuId)) {
				seen.add(menuId)
				visibleIds.add(menuId)
				const parentId = menuById.get(menuId)?.parent_id
				menuId = parentId == null ? null : Number(parentId)
			}
		}

		return (menu || []).filter((item) => visibleIds.has(Number(item.id)))
	}, [menu, data])

	const treeRows = useMemo(
		() => buildMenuProductTreeRows(reportMenu, data, expandedMenuIds, ''),
		[reportMenu, data, expandedMenuIds]
	)

	const menuProductCounts = useMemo(() => {
		const counts = buildMenuProductCounts(reportMenu, data)
		const normalized = new Map()
		for (const [key, value] of counts) {
			normalized.set(Number(key), value)
		}
		return normalized
	}, [reportMenu, data])

	function expandAllMenus() {
		setExpandedMenuIds(new Set(reportMenu.map((item) => item.id)))
	}

	function collapseAllMenus() {
		setExpandedMenuIds(new Set())
	}

	useEffect(() => {
		getPoses()
		getMenu()
		getCategories()
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header">
				<h4 className="vertical-center h-100">
					{t('calculation')} [{data.length}]
				</h4>
			</div>

			<div className="card">
				<div className="card-body">

					<div className="d-flex flex-wrap gap-2 mb-3">
						<div>
							<FilterSearch
								value={filterData.search}
								onSearchChange={handleSearchChange}>
							</FilterSearch>
						</div>
						<div>
							<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
								<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
									{t('filter')}
								</Dropdown.Toggle>

								<Dropdown.Menu className="dropdown-filter">
									<div className="row">
										<div className="form-group col-md-6">
											<label>{t('pos')}</label>
											<Select
												options={poses}
												value={poses.find(option => option.id === filterData.pos_id) || ''}
												onChange={(option) => setFilterData({ ...filterData, 'pos_id': option.id })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
										<div className="form-group col-md-6">
											<label>{t('menu')}</label>
											<Select
												isMulti
												isClearable
												options={menu}
												value={filterData.menu_ids}
												onChange={(options) => setFilterData({ ...filterData, 'menu_ids': options ? options : null })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
									</div>
									<div className="row">
										<div className="form-group col-md-6">
											<label>{t('category')}</label>
											<Select
												options={categories}
												value={categories.find(option => option.id === filterData.category_id) || ''}
												onChange={(option) => setFilterData({ ...filterData, 'category_id': option.id })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
										<div className="form-group col-md-6">
											<label>{t('date')}</label>
											<div className="calendar-input">
												<DatePicker
													showMonthDropdown
													showYearDropdown
													dropdownMode="select"
													yearDropdownItemNumber={2}
													minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
													maxDate={new Date(new Date().getFullYear(), 11, 31)}
													className="form-control"
													dateFormat="dd.MM.yyyy"
													selected={filterData.date ? new Date(filterData.date) : ''}
													onChange={(date) => setFilterData({ ...filterData, 'date': formatDate(date, 'yyyy-MM-dd') })} />
												<i className="uil uil-calendar-alt"></i>
											</div>
										</div>
									</div>
									<div className="row mt-3">
										<div className="col-md-12">
											<div className="d-flex justify-content-end gap-2">
												<button className="btn btn-secondary" onClick={() => setDropdownIsOpen(false)}>{t('cancel')}</button>
												<button className="btn btn-primary" onClick={() => searchWithFilter()}>{t('apply')}</button>
											</div>
										</div>
									</div>
								</Dropdown.Menu>
							</Dropdown>
						</div>
						<div className="ms-auto d-flex gap-2">
							<button type="button" className="btn btn-outline-primary" onClick={expandAllMenus}>
								<i className="uil uil-expand-arrows-alt me-1"></i>
								{t('expand_all')}
							</button>
							<button type="button" className="btn btn-outline-secondary" onClick={collapseAllMenus}>
								<i className="uil uil-compress-arrows me-1"></i>
								{t('collapse_all')}
							</button>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{filterData.pos_id &&
									<span className="me-2">
										<b>{t('pos')}: </b>
										{findKeyFromArrayByValue(poses, 'id', filterData.pos_id, 'name')}
									</span>
								}
								{filterData.category_id &&
									<span className="me-2">
										<b>{t('category')}: </b>
										{findKeyFromArrayByValue(categories, 'id', filterData.category_id, 'name')}
									</span>
								}
								{filterData.menu_ids?.length > 0 &&
									<span className="me-2">
										<b>{t('menu')}: </b>
										{filterData.menu_ids.map((item) => item.name).join(', ')}
									</span>
								}
								{filterData.date &&
									<span className="me-2">
										<b>{t('date')}: </b>
										{formatDate(filterData.date, 'dd.MM.yyyy')}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('reset')}</button>
							</div>
						</div>
					}

					<div className="d-flex flex-wrap align-items-center gap-2 mb-3">
						<span className="badge bg-primary">{t('products')}: {data.length}</span>
						<span className="badge bg-danger">
							{t('stop_list')}: {data.filter((item) => Number(item.balance) <= 0).length}
						</span>
					</div>

					<div className="table-responsive">
						<table className="table table-bordered mb-0 report-calculation-table">
							<thead>
								<tr>
									<th style={{ 'width': '55%' }}>{t('name2')}</th>
									<th style={{ 'width': '120px' }} className="text-end">{t('ingredients')}</th>
									<th style={{ 'width': '140px' }} className="text-end">{t('quantity')}</th>
								</tr>
							</thead>
							<tbody>
								{treeRows.map((row) => {
									if (row.rowType === 'menu') {
										const canToggle = row.hasChildren
										const indent = row.depth * 18
										return (
											<tr key={row.key} className="products-tree-menu-row">
												<td
													className={canToggle ? 'products-tree-menu-name-cell' : ''}
													onClick={canToggle ? () => toggleMenu(row.collapseKey) : undefined}
													onKeyDown={canToggle ? (event) => {
														if (event.key === 'Enter' || event.key === ' ') {
															event.preventDefault()
															toggleMenu(row.collapseKey)
														}
													} : undefined}
													role={canToggle ? 'button' : undefined}
													tabIndex={canToggle ? 0 : undefined}>
													<div className="d-flex align-items-center" style={{ paddingLeft: `${indent}px` }}>
														{canToggle &&
															<span className="products-tree-toggle me-1 text-dark">
																<i className={`uil uil-angle-${row.isCollapsed ? 'right' : 'down'}`}></i>
															</span>
														}
														{!canToggle &&
															<span className="products-tree-toggle-spacer me-1"></span>
														}
														<i className="uil uil-folder me-1 text-warning"></i>
														<strong>{row.menu.name}</strong>
														<span className="report-calculation-menu-count ms-2">
															{menuProductCounts.get(Number(row.menu.id)) || 0}
														</span>
													</div>
												</td>
												<td></td>
												<td></td>
											</tr>
										)
									}

									const product = row.product
									const isStop = Number(product.balance) <= 0
									return (
										<tr key={row.key}>
											<td>
												<div className="d-flex align-items-center"
													style={{ paddingLeft: `${row.depth * 18}px` }}>
													<span className="products-tree-toggle-spacer me-1"></span>
													<button type="button"
														className="btn btn-link p-0 text-start report-calculation-product-name"
														onClick={() => setSelectedProduct({
															id: product.id,
															name: product.name,
															pos_id: filterData.pos_id || reduxAccount.pos_id,
														})}>
														{product.name}
													</button>
												</div>
											</td>
											<td className="text-end text-muted">{product.ingredients?.length || 0}</td>
											<td className={`text-end fw-bold ${isStop ? 'text-danger' : ''}`}>
												{formatMoney(product.balance)}
											</td>
										</tr>
									)
								})}
								{treeRows.length === 0 &&
									<tr>
										<td colSpan="3" className="text-center text-muted py-4">{t('list_empty')}</td>
									</tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<Modal show={!!selectedProduct} animation={false} size="xl" centered
				dialogClassName="report-calculation-product-modal"
				onHide={() => setSelectedProduct(null)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('product')}: {selectedProduct?.name}</Modal.Title>
				</Modal.Header>
				<Modal.Body className="p-2" style={{ maxHeight: '86vh', overflowY: 'auto', backgroundColor: 'var(--dark-surface-2, #f5f6f8)' }}>
					{selectedProduct &&
						<ProductCreateUpdate
							productId={selectedProduct.id}
							productPosId={selectedProduct.pos_id}
							embedded
							initialMainTab={2}
							onClose={() => setSelectedProduct(null)}
						/>
					}
				</Modal.Body>
			</Modal>
		</>
	)
}

export default Calculation